import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Textarea } from '@/components/ui-kit/textarea';
import { Button } from '@/components/ui-kit/button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAddAgent } from '@/modules/gpt-chats/hooks/use-agents';
import { useState } from 'react';
import {
  getAddAgentSchema,
  AddAgentFormValues,
  addAgentDefaultValues,
  PREDEFINED_PROMPTS,
  AGENT_TYPE_FALSE_NAME,
  AGENT_TYPE_FALSE_DESCRIPTION,
  AGENT_TYPE_TRUE_NAME,
  AGENT_TYPE_TRUE_DESCRIPTION,
} from './utils';
import { Info, RefreshCcw } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';

export const AddAgent = () => {
  const [open, onOpenChange] = useState(false);
  const [showDescriptionStep, setShowDescriptionStep] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const { isPending, mutateAsync } = useAddAgent();
  const { t } = useTranslation();

  const { toast } = useToast();

  const form = useForm<AddAgentFormValues>({
    defaultValues: addAgentDefaultValues,
    resolver: zodResolver(getAddAgentSchema(t)),
  });

  const handleSubmit = async (formValues: AddAgentFormValues) => {
    try {
      const payload = {
        name: formValues.name,
        description: formValues.description || undefined,
        create_from: formValues.create_from || undefined,
        is_any_agent: formValues.agent_type,
      };

      const res = await mutateAsync(payload);
      if (!res.is_success) {
        toast({
          title: t('ERROR'),
          variant: 'destructive',
          description: t('SOMETHING_WENT_WRONG'),
        });
      }
      toast({
        title: t('SUCCESS'),
        variant: 'success',
        description: t('AGENT_CREATED_SUCCESSFULLY'),
      });
      form.reset();
      setSelectedPrompt(null);
      onOpenChange(false);
    } catch (error) {
      return toast({
        title: t('ERROR'),
        variant: 'destructive',
        description: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handlePromptSelect = (prompt: (typeof PREDEFINED_PROMPTS)[number]) => {
    setSelectedPrompt(prompt.value);
    form.setValue('create_from', prompt.value);
    form.setValue('description', prompt.description);
  };

  const handleCreateFromTemplate = async () => {
    if (selectedPrompt) {
      await handleSubmit({
        name: '',
        description: form.getValues('description'),
        create_from: selectedPrompt as AddAgentFormValues['create_from'],
        agent_type: form.getValues('agent_type'),
      });
    }
  };

  const handleCustomAgentClick = () => {
    setSelectedPrompt(null);
    form.setValue('create_from', undefined);
    form.setValue('description', '');
    setShowDescriptionStep(true);
  };

  const handleBack = () => {
    setShowDescriptionStep(false);
  };

  const handleModalClose = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setShowDescriptionStep(false);
      setSelectedPrompt(null);
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogTrigger asChild>
        {/* <Button variant="ghost" size="icon" title="Create an agent">
          <Plus className="h-4 w-4" />
        </Button> */}
        <div>
          <Button className={cn('w-full justify-center  px-4  h-auto mt-4')}>
            <span>{t('ADD_AGENT')}</span>
          </Button>
        </div>
      </DialogTrigger>
      <DialogContent className="md:max-w-[1024px]">
        <Form {...form}>
          {!showDescriptionStep ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-semibold">
                  {t('SET_UP_YOUR_AGENT')}
                </DialogTitle>
                <DialogDescription className="text-base">
                  {t('SET_UP_YOUR_AGENT_DESCRIPTION')}
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-6 py-6">
                <div
                  onClick={handleCustomAgentClick}
                  className="group flex-1 cursor-pointer rounded-xl border bg-accent/50 p-8 transition-all hover:bg-accent"
                >
                  <div className="flex flex-col items-center gap-3 text-center">
                    <RefreshCcw className="h-10 w-10 text-muted-foreground transition-colors group-hover:text-foreground" />
                    <h3 className="text-xl font-semibold">{t('CREATE_CUSTOM_AGENT')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('CREATE_CUSTOM_AGENT_DESCRIPTION')}
                    </p>
                  </div>
                </div>

                <div className="flex-[2]">
                  <h3 className="mb-4 text-xl font-semibold">{t('PREDEFINED_PROMPTS_TITLE')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {PREDEFINED_PROMPTS.map((prompt) => (
                      <button
                        key={prompt.value}
                        type="button"
                        onClick={() => handlePromptSelect(prompt)}
                        className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-1.5 text-sm font-medium transition-all ${
                          selectedPrompt === prompt.value
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background hover:border-border hover:bg-accent'
                        }`}
                      >
                        {t(prompt.label)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <FormField
                control={form.control}
                name="agent_type"
                render={({ field }) => (
                  <FormItem className="space-y-4 pb-6 transition-all duration-300 animate-in fade-in zoom-in-95">
                    <FormLabel className="text-lg font-semibold">
                      {t('SELECT_AGENT_TYPE')}
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(val) => field.onChange(val === 'true')}
                        value={field.value ? 'true' : 'false'}
                        className="grid grid-cols-2 gap-4"
                      >
                        <FormItem>
                          <div className="relative">
                            <FormControl>
                              <RadioGroupItem
                                value="false"
                                className="peer absolute left-4 top-4"
                              />
                            </FormControl>
                            <FormLabel className="flex cursor-pointer flex-col gap-1 rounded-lg border p-4 pl-10 transition-colors duration-200 hover:bg-accent/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5">
                              <span className="font-semibold text-foreground">
                                {t(AGENT_TYPE_FALSE_NAME)}
                              </span>
                              <span className="text-sm font-normal text-muted-foreground">
                                {t(AGENT_TYPE_FALSE_DESCRIPTION)}
                              </span>
                            </FormLabel>
                          </div>
                        </FormItem>

                        <FormItem>
                          <div className="relative">
                            <FormControl>
                              <RadioGroupItem value="true" className="peer absolute left-4 top-4" />
                            </FormControl>
                            <FormLabel className="flex cursor-pointer flex-col gap-1 rounded-lg border p-4 pl-10 transition-colors duration-200 hover:bg-accent/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5">
                              <span className="font-semibold text-foreground">
                                {t(AGENT_TYPE_TRUE_NAME)}
                              </span>
                              <span className="text-sm font-normal text-muted-foreground">
                                {t(AGENT_TYPE_TRUE_DESCRIPTION)}
                              </span>
                            </FormLabel>
                          </div>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-3">
                <DialogClose asChild>
                  <Button variant="outline">{t('CANCEL')}</Button>
                </DialogClose>
                <Button onClick={handleCreateFromTemplate} disabled={!selectedPrompt || isPending}>
                  {isPending ? t('CREATING') : t('CREATE')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-semibold">{t('DESCRIBE_AGENT')}</DialogTitle>
                <DialogDescription className="text-base">
                  {t('DESCRIBE_AGENT_DESCRIPTION')}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-normal">
                        {t('AGENT_NAME')}
                        <span className="text-error">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder={t('AGENT_NAME_PLACEHOLDER')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-normal">
                        {t('DESCRIPTION')}
                        <span className="text-error">*</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea rows={8} placeholder={t('DESCRIPTION_PLACEHOLDER')} {...field} />
                      </FormControl>
                      {!selectedPrompt && (
                        <FormDescription>{t('CUSTOM_DESCRIPTION_HINT')}</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="agent_type"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-base font-normal">{t('AGENT_TYPE')}</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={(val) => field.onChange(val === 'true')}
                          value={field.value ? 'true' : 'false'}
                          className="flex gap-6"
                        >
                          <FormItem className="space-y-0">
                            <div className="flex cursor-pointer items-center space-x-2">
                              <FormControl>
                                <RadioGroupItem value="false" id="sequential-minimal" />
                              </FormControl>
                              <FormLabel
                                htmlFor="sequential-minimal"
                                className="flex cursor-pointer flex-row items-center gap-2 font-normal"
                              >
                                {t(AGENT_TYPE_FALSE_NAME)}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{t(AGENT_TYPE_FALSE_DESCRIPTION)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </FormLabel>
                            </div>
                          </FormItem>

                          <FormItem className="space-y-0">
                            <div className="flex cursor-pointer items-center space-x-2">
                              <FormControl>
                                <RadioGroupItem value="true" id="reasoning-minimal" />
                              </FormControl>
                              <FormLabel
                                htmlFor="reasoning-minimal"
                                className="flex cursor-pointer flex-row items-center gap-2 font-normal"
                              >
                                {t(AGENT_TYPE_TRUE_NAME)}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{t(AGENT_TYPE_TRUE_DESCRIPTION)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </FormLabel>
                            </div>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-3">
                  <Button variant="outline" type="button" onClick={handleBack}>
                    {t('CANCEL')}
                  </Button>
                  <Button type="submit" disabled={isPending || !form.formState.isValid}>
                    {isPending ? t('CREATING') : t('CREATE')}
                  </Button>
                </div>
              </form>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  );
};
